import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AttainmentNumberCalculator = () => {
  const [birthDate, setBirthDate] = useState('');
  const [attainmentNumbers, setAttainmentNumbers] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  // Helper function to reduce numbers to single digit
  const reduceNumber = (num) => {
    while (num > 9 && ![11, 22, 33].includes(num)) {
      num = num.toString().split('').reduce((sum, digit) => sum + +digit, 0);
    }
    return num;
  };

  const handleInputChange = (e) => {
    setBirthDate(e.target.value);
    setError('');
  };

  const calculateAttainmentNumbers = () => {
    if (!birthDate.trim()) {
      setError('Please enter your birth date');
      return;
    }

    const [year, month, day] = birthDate.split('-');
    const yearDigits = year.split('');
    const monthDigits = month.split('');
    const dayDigits = day.split('');

    // Calculate Attainment Numbers
    const calculateAttainment = (first, second) => {
      return reduceNumber(parseInt(first) + parseInt(second));
    };

    // First Attainment: Month 1 + Month 2
    const firstAttainment = calculateAttainment(monthDigits[0], monthDigits[1]);
    
    // Second Attainment: Day 1 + Day 2
    const secondAttainment = calculateAttainment(dayDigits[0], dayDigits[1]);
    
    // Third Attainment: Year 1 + Year 2
    const thirdAttainment = calculateAttainment(yearDigits[0], yearDigits[1]);
    
    // Fourth Attainment: First Attainment + Second Attainment
    const fourthAttainment = calculateAttainment(firstAttainment, secondAttainment);

    setAttainmentNumbers({
      first: firstAttainment,
      second: secondAttainment,
      third: thirdAttainment,
      fourth: fourthAttainment,
      yearDigits,
      monthDigits,
      dayDigits
    });

    // Generate chart data
    setChartData({
      labels: ['First', 'Second', 'Third', 'Fourth'],
      datasets: [{
        label: 'Attainment Numbers',
        data: [firstAttainment, secondAttainment, thirdAttainment, fourthAttainment],
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(153, 102, 255, 0.5)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)'
        ],
        borderWidth: 1
      }]
    });
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Attainment Number Calculator | Discover Your Life Goals</title>
        <meta name="description" content="Calculate your Attainment Numbers using our Numerology Calculator. Understand the goals and achievements you're meant to accomplish in life." />
        <meta name="keywords" content="attainment numbers, numerology calculator, life goals, personal achievement, spiritual journey" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/calculator/attainment-numbers" />
        <meta property="og:title" content="Attainment Number Calculator | Discover Your Life Goals" />
        <meta property="og:description" content="Calculate your Attainment Numbers using our Numerology Calculator. Understand the goals and achievements you're meant to accomplish in life." />
        <meta property="og:image" content="https://yourwebsite.com/images/attainment-og-image.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourwebsite.com/calculator/attainment-numbers" />
        <meta property="twitter:title" content="Attainment Number Calculator | Discover Your Life Goals" />
        <meta property="twitter:description" content="Calculate your Attainment Numbers using our Numerology Calculator. Understand the goals and achievements you're meant to accomplish in life." />
        <meta property="twitter:image" content="https://yourwebsite.com/images/attainment-og-image.jpg" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🔮</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Attainment Number Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Discover Your Life Goals</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="birthDate">
            Birth Date:
          </label>
          <input
            type="date"
            id="birthDate"
            name="birthDate"
            value={birthDate}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
          />
        </div>

        <button
          onClick={calculateAttainmentNumbers}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Attainment Numbers
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {attainmentNumbers && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Attainment Numbers</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(attainmentNumbers).map(([key, value]) => (
                <div key={key} className="p-6 bg-white rounded-lg shadow-md">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-purple-600 mb-2">
                      {value}
                    </div>
                    <p className="text-gray-700">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                  </div>
                </div>
              ))}
            </div>

            {chartData && (
              <div className="space-y-8 mt-12">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Attainment Number Distribution</h4>
                  <div className="h-96">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Attainment Numbers Overview'
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="text-xl font-semibold mb-4">Understanding Your Attainments</h4>
                  <div className="space-y-4">
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <p className="font-medium">First Attainment (Month 1 + Month 2)</p>
                      <p>Represents your personal goals and achievements</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <p className="font-medium">Second Attainment (Day 1 + Day 2)</p>
                      <p>Signifies your career and professional goals</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <p className="font-medium">Third Attainment (Year 1 + Year 2)</p>
                      <p>Represents your spiritual and life goals</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <p className="font-medium">Fourth Attainment (First + Second)</p>
                      <p>Signifies your overall life purpose and goals</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="text-xl font-semibold mb-4">Calculation Breakdown</h4>
                  <div className="space-y-6">
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <h5 className="font-semibold mb-2">1. First Attainment</h5>
                      <p className="text-gray-700">{attainmentNumbers.monthDigits[0]} + {attainmentNumbers.monthDigits[1]} = {attainmentNumbers.first}</p>
                      <p className="text-sm text-gray-600">Represents your personal goals and achievements</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <h5 className="font-semibold mb-2">2. Second Attainment</h5>
                      <p className="text-gray-700">{attainmentNumbers.dayDigits[0]} + {attainmentNumbers.dayDigits[1]} = {attainmentNumbers.second}</p>
                      <p className="text-sm text-gray-600">Signifies your career and professional goals</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <h5 className="font-semibold mb-2">3. Third Attainment</h5>
                      <p className="text-gray-700">{attainmentNumbers.yearDigits[0]} + {attainmentNumbers.yearDigits[1]} = {attainmentNumbers.third}</p>
                      <p className="text-sm text-gray-600">Represents your spiritual and life goals</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg shadow-inner">
                      <h5 className="font-semibold mb-2">4. Fourth Attainment</h5>
                      <p className="text-gray-700">{attainmentNumbers.first} + {attainmentNumbers.second} = {attainmentNumbers.fourth}</p>
                      <p className="text-sm text-gray-600">Signifies your overall life purpose and goals</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your Attainment Numbers:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Birth Date</h3>
              <p className="text-gray-700">
                Input your complete birth date in the format YYYY-MM-DD.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate" button to get your Attainment Numbers.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review your Attainment Numbers and their meanings in the results section.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What are Attainment Numbers?</h3>
              <p className="text-gray-700">
                Attainment Numbers in numerology reveal the goals, achievements, and purposes you're meant to accomplish in life.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How are they calculated?</h3>
              <p className="text-gray-700">
                They are calculated by adding pairs of digits from your birth date and reducing them to single digits.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What do they reveal?</h3>
              <p className="text-gray-700">
                They show your personal, professional, spiritual, and overall life goals and achievements.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Accurate Attainment Number calculation</li>
                <li>Visual representation of all attainments</li>
                <li>Goal-based interpretation of each attainment</li>
                <li>Clean and intuitive interface</li>
                <li>Detailed explanation of results</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand personal goals</li>
                <li>Set career objectives</li>
                <li>Discover life purpose</li>
                <li>Gain insights into achievements</li>
                <li>Develop strategies to achieve goals</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttainmentNumberCalculator;
